/**
 * Karta ewidencji czasu pracy (Etap 39, ADR-0040).
 *
 * Ewidencję prowadzi się z art. 149 § 1 k.p., a jej treść opisuje § 6 pkt 1
 * lit. a rozporządzenia o dokumentacji pracowniczej. Kartę wystawia się za
 * miesiąc i przechowuje OSOBNO od akt osobowych (art. 94 pkt 9a k.p.) — dlatego
 * jest tu własna akcja, a nie kolejny szablon w kreatorze dokumentów.
 *
 * Po wystawieniu pokazujemy podsumowanie, w tym dwa ostrzeżenia, które karta
 * niesie wprost: brakujące godziny rozpoczęcia albo zakończenia pracy i wpisy
 * czekające na akceptację przełożonego.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Input, Spinner, useToast } from '@k1p4/ui'
import { api, apiMonth } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface RecordCardProps {
  employeeId: string
  /** Wystawienie karty wymaga uprawnienia do ewidencji czasu pracy. */
  canIssue: boolean
}

/** Miesiąc bieżący w formacie rrrr-mm — domyślna wartość formularza. */
function currentMonth(): string {
  return apiMonth()
}

export function RecordCard({ employeeId, canIssue }: RecordCardProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const monthId = useId()

  const [month, setMonth] = useState(currentMonth)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<
    | {
        workedHours: number
        overtimeHours: number
        nightHours: number
        leaveDays: number
        sickDays: number
        unjustifiedDays: number
        unjustifiedHours: number
        missingDays: number
        dayOffInLieuDays: number
        dutyHours: number
        missingClockTimes: boolean
        pendingApproval: boolean
      }
    | null
  >(null)

  const issue = useMutation({
    mutationFn: () => api.issueRecordCard({ employeeId, month }),
    onSuccess: (result) => {
      setSummary(result.totals)
      setError(null)
      toast.show(t('recordCard.issued'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: () => setError('recordCard.errorGeneric'),
  })

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('recordCard.title')}</h3>
        {canIssue ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={issue.isPending}
            onClick={() => {
              setError(null)
              issue.mutate()
            }}
          >
            {issue.isPending ? <Spinner label={t('recordCard.issuing')} /> : t('recordCard.issue')}
          </Button>
        ) : null}
      </div>
      <p className={styles.note}>{t('recordCard.lead')}</p>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <div className={styles.formGrid}>
        <FormField label={t('recordCard.month')} htmlFor={monthId} hint={t('recordCard.monthHint')}>
          <Input
            id={monthId}
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </FormField>
      </div>

      {summary !== null ? (
        <InfoBanner
          tone={summary.missingClockTimes || summary.pendingApproval ? 'warning' : 'info'}
          className={styles.banner}
        >
          {t('recordCard.summary', {
            hours: summary.workedHours,
            overtime: summary.overtimeHours,
            night: summary.nightHours,
          })}
          {summary.leaveDays + summary.sickDays > 0
            ? ` ${t('recordCard.absences', { leave: summary.leaveDays, sick: summary.sickDays })}`
            : ''}
          {summary.unjustifiedDays > 0
            ? ` ${t('recordCard.unjustified', {
                count: summary.unjustifiedDays,
                hours: summary.unjustifiedHours,
              })}`
            : ''}
          {summary.dayOffInLieuDays > 0
            ? ` ${t('recordCard.daysOffInLieu', { count: summary.dayOffInLieuDays })}`
            : ''}
          {summary.dutyHours > 0 ? ` ${t('recordCard.duty', { hours: summary.dutyHours })}` : ''}
          {summary.missingDays > 0
            ? ` ${t('recordCard.missingDays', { count: summary.missingDays })}`
            : ''}
          {summary.missingClockTimes ? ` ${t('recordCard.missingClock')}` : ''}
          {summary.pendingApproval ? ` ${t('recordCard.pending')}` : ''}
        </InfoBanner>
      ) : null}
    </section>
  )
}
